/* ------------------------------------------------------------------------- *
 * SHOWQ — shared Asia/Kuwait clock + delivery calendar.
 *
 * Two pure exports. No DOM, no fetch, no settings, no dependencies:
 *   window.ShowqTime      the clock  (Intl.formatToParts, server-skew corrected)
 *   window.ShowqCalendar  build(cfg, state) -> [{ iso, slots, ok, reason }]
 *
 * WHY THIS FILE EXISTS. The cart picker (snippets/showq-delivery.liquid) and the
 * product-page promise (snippets/showq-delivery-promise.liquid) must never disagree
 * about what day it is or which day is the first bookable one. There is exactly one
 * copy of that arithmetic and it is here. Nobody forks it.
 *
 * Both exports install only if absent, so an older inline copy can never clobber a
 * newer asset and loading this file twice is harmless.
 *
 * ALL computation is Asia/Kuwait. The device timezone is never read.
 * ------------------------------------------------------------------------- */
(function (w) {
  'use strict';

  /* ---------------------------------------------------------------- *
   * 1. ShowqTime — the clock
   * ---------------------------------------------------------------- */
  if (!w.ShowqTime) {
    var TZ = 'Asia/Kuwait';
    var ok = false, DTF = null;
    try {
      ok = typeof Intl !== 'undefined' && !!(Intl.DateTimeFormat.prototype.formatToParts);
      if (ok) {
        DTF = new Intl.DateTimeFormat('en-CA', {
          timeZone: TZ, hourCycle: 'h23',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
      }
    } catch (e) { ok = false; }

    var parts = function (ms) {
      var o = {}, list = DTF.formatToParts(new Date(ms)), i;
      for (i = 0; i < list.length; i++) if (list[i].type !== 'literal') o[list[i].type] = +list[i].value;
      if (o.hour === 24) o.hour = 0;
      return o;
    };
    var offsetMs = function (ms) {
      var p = parts(ms);
      return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
    };
    var civil = function (ms) {
      var p = parts(ms);
      var anchor = Date.UTC(p.year, p.month - 1, p.day);
      return {
        y: p.year, m: p.month, d: p.day, hh: p.hour, mm: p.minute,
        iso: new Date(anchor).toISOString().slice(0, 10),
        dow: new Date(anchor).getUTCDay()
      };
    };
    var toEpoch = function (iso, hhmm) {
      var d = iso.split('-').map(Number), t = hhmm.split(':').map(Number);
      var guess = Date.UTC(d[0], d[1] - 1, d[2], t[0], t[1], 0);
      var ts = guess - offsetMs(guess);
      var off2 = offsetMs(ts);
      if (guess - off2 !== ts) ts = guess - off2;
      return ts;
    };
    var addDays = function (iso, n) {
      var d = iso.split('-').map(Number);
      return new Date(Date.UTC(d[0], d[1] - 1, d[2] + n)).toISOString().slice(0, 10);
    };
    var dowOf = function (iso) {
      var d = iso.split('-').map(Number);
      return new Date(Date.UTC(d[0], d[1] - 1, d[2])).getUTCDay();
    };

    var SKEW = 0;
    /* The buyer's device clock can be wrong; the server's is not. Only ever trust an epoch
       that came off a page rendered moments ago — a cached page would freeze the clock in
       the past, which is exactly how a delivery date goes stale without anyone noticing.
       maxAgeMs lets a cacheable surface (a product page) refuse a stale stamp outright. */
    w.ShowqTimeInit = function (serverEpochSeconds, maxAgeMs) {
      var s = Number(serverEpochSeconds) * 1000;
      if (!s) return;
      var delta = s - Date.now();
      if (Math.abs(delta) >= 86400000) return;
      if (maxAgeMs && -delta > maxAgeMs) return;   /* page is older than the caller tolerates */
      SKEW = delta;
    };
    var now = function () { return Date.now() + SKEW; };

    var localeTag = function () {
      var l = (document.documentElement.lang || 'en').toLowerCase();
      return l.indexOf('ar') === 0 ? 'ar-KW-u-nu-latn' : l;
    };
    var fmt = function (opts, ms) {
      return new Intl.DateTimeFormat(localeTag(), opts).format(new Date(ms));
    };
    var formatDate = function (iso, opts) {
      var o = { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' }, k;
      if (opts) for (k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k];
      o.timeZone = TZ;
      return fmt(o, toEpoch(iso, '12:00'));
    };
    var formatTime = function (iso, hhmm) {
      return fmt({ timeZone: TZ, hour: 'numeric', minute: '2-digit' }, toEpoch(iso, hhmm));
    };

    w.ShowqTime = {
      TZ: TZ, now: now, civil: civil, toEpoch: toEpoch, addDays: addDays, dowOf: dowOf,
      formatDate: formatDate, formatTime: formatTime, localeTag: localeTag, supported: ok
    };
  }

  /* ---------------------------------------------------------------- *
   * 2. ShowqCalendar — which days are bookable, and with which slots
   *
   * build(cfg, state) is PURE: same inputs, same answer, no DOM read.
   *   cfg   the JSON island emitted by snippets/showq-delivery-calendar.liquid
   *   state { applicable, minLead, sameDayAllowed }
   *         applicable     false -> [] (a cart with nothing to deliver; a gift-card PDP)
   *         minLead        made-to-order floor in whole days
   *         sameDayAllowed false -> today is never offered. A promise about same-day is a
   *                        promise about a DESTINATION, and only the caller knows whether it
   *                        has one. The product page never does, so it always passes false.
   * Returns [] rather than throwing on every unusable input — a broken calendar must show
   * nothing, never a wrong date, and must never block a sale.
   * ---------------------------------------------------------------- */
  if (!w.ShowqCalendar) {
    var SLOT_RE = /^(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})$/;
    var BOOKING_RE = /^(\d{4}-\d{2}-\d{2})[|\s]+(\d{2}:\d{2}-\d{2}:\d{2})$/;
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };

    /* strict, silent on malformed: a mistyped slot line is ignored, never guessed at */
    function parseSlots(lines) {
      var out = [], i, raw, bits, range, label, m, sh, sm, eh, em;
      lines = lines || [];
      for (i = 0; i < lines.length; i++) {
        raw = String(lines[i]).replace(/\r/g, '').trim();
        if (!raw) continue;
        bits = raw.split('|');
        range = bits[0].trim();
        label = bits.length > 1 ? bits.slice(1).join('|').trim() : '';
        m = SLOT_RE.exec(range);
        if (!m) continue;
        sh = +m[1]; sm = +m[2]; eh = +m[3]; em = +m[4];
        if (sh > 23 || eh > 23 || sm > 59 || em > 59) continue;
        if (eh * 60 + em <= sh * 60 + sm) continue;      /* no midnight wrap, no zero length */
        out.push({
          key: pad(sh) + ':' + pad(sm) + '-' + pad(eh) + ':' + pad(em),
          start: pad(sh) + ':' + pad(sm),
          end: pad(eh) + ':' + pad(em),
          label: label
        });
      }
      return out;
    }

    /* Capacity is a SECOND, additive source of "this slot is full". The manual list
       (delivery_availability.full_slots) is evaluated first and always wins. EVERY guard here
       fails OPEN: a missing, empty, mistyped or malformed counter closes nothing, because a
       broken counter must never stop the shop taking orders. */
    function readCapacity(cfg) {
      if (cfg._sqCap) return cfg._sqCap;
      var cap = (cfg.capacity && typeof cfg.capacity === 'object') ? cfg.capacity : {};
      var limit = Number(cap.limit);
      if (!isFinite(limit) || limit <= 0) limit = 0;
      var counts = (limit > 0 && cap.counts && typeof cap.counts === 'object') ? cap.counts : null;
      var tally = null;
      if (limit > 0 && typeof cap.bookings === 'string' && cap.bookings) {
        /* Shopify Flow can append a line but cannot increment a number, so it writes one
           "YYYY-MM-DD|HH:MM-HH:MM" per booked order and we recount here on every build.
           A full recount self-heals: a duplicated or junk line can never corrupt a running total. */
        var lines = cap.bookings.split(/\r?\n/), t = {}, hit = 0, i, m2, k;
        for (i = 0; i < lines.length; i++) {
          m2 = BOOKING_RE.exec(lines[i].trim());
          if (!m2) continue;
          k = m2[1] + '|' + m2[2];
          t[k] = (t[k] || 0) + 1;
          hit++;
        }
        if (hit) tally = t;
      }
      cfg._sqCap = { limit: limit, counts: counts, tally: tally };
      return cfg._sqCap;
    }

    function capFull(cap, iso, slotKey) {
      if (!cap.counts && !cap.tally) return false;
      var bucket = cap.counts ? cap.counts[iso] : null;
      if (bucket && typeof bucket !== 'object') bucket = null;
      var n;
      if (bucket) n = bucket[slotKey];                                          /* nested shape */
      if ((n === undefined || n === null) && cap.counts) n = cap.counts[iso + ' ' + slotKey];
      if ((n === undefined || n === null) && cap.counts) n = cap.counts[iso + '|' + slotKey];
      if ((n === undefined || n === null) && cap.tally) n = cap.tally[iso + '|' + slotKey];
      if (n === undefined || n === null) return false;
      n = Number(n);
      if (!isFinite(n) || n <= 0) return false;
      return n >= cap.limit;
    }

    function build(cfg, state) {
      state = state || {};
      var T = w.ShowqTime;
      if (!cfg || cfg.enabled === false) return [];
      if (state.applicable === false) return [];
      if (!T || !T.supported) return [];

      var SLOTS = parseSlots(cfg.slotLines);
      if (!SLOTS.length) return [];

      var nowMs = T.now();
      var today = T.civil(nowMs);
      var earliestMs = nowMs + (Number(cfg.leadHours) || 0) * 3600000;

      var nowMinutes = today.hh * 60 + today.mm;
      var cutoffMinutes = (Number(cfg.cutoffHour) || 0) * 60 + (Number(cfg.cutoffMinute) || 0);
      var afterCutoff = nowMinutes >= cutoffMinutes;            /* INCLUSIVE at the cutoff */

      var dayOffset = 0;
      if (afterCutoff || !cfg.sameDayEnabled) dayOffset = 1;
      if (state.sameDayAllowed === false) dayOffset = Math.max(dayOffset, 1);
      dayOffset = Math.max(dayOffset, Number(state.minLead) || 0);

      var closed = cfg.closedWeekdays || [];
      var black = cfg.blackoutDates || [];
      var AV = cfg.availability || {};
      var cap = readCapacity(cfg);

      var days = [], i, j, iso, reason, open, soldOut;

      for (i = 0; i <= (Number(cfg.daysAhead) || 0); i++) {
        iso = T.addDays(today.iso, i);
        if (i < dayOffset) continue;                            /* lead-blocked: omitted, never shown */

        reason = '';
        if (closed.indexOf(T.dowOf(iso)) !== -1) reason = 'closed';
        else if (black.indexOf(iso) !== -1) reason = 'blackout';
        else if (AV[iso] && AV[iso].closed === true) reason = 'full';   /* ops closed this date */

        open = [];
        if (!reason) {
          soldOut = (AV[iso] && AV[iso].fullSlots) || [];
          for (j = 0; j < SLOTS.length; j++) {
            /* A slot stays bookable while there is still `leadHours` of runway before it CLOSES.
               Comparing against the window's START retired the morning slot at 10:00 sharp and
               killed same-day at 16:00 — a full hour before the 17:00 cut-off could ever fire, so
               the cut-off setting was dead and an hour of same-day orders was lost every day.
               With leadHours=2 and slots 10:00-16:00 / 16:00-22:00 this produces the stated rule:
                 before 14:00  both slots today
                 14:00-17:00   evening slot only (the morning window is inside its notice period)
                 from 17:00    no same-day at all (dayOffset above already removes today)
               Future days are unaffected: their windows close far beyond now + lead. */
            if (T.toEpoch(iso, SLOTS[j].end) < earliestMs) continue;
            if (soldOut.indexOf(SLOTS[j].key) !== -1) continue;
            if (SLOTS[j].label && soldOut.indexOf(SLOTS[j].label) !== -1) continue;
            if (capFull(cap, iso, SLOTS[j].key)) continue;
            open.push(SLOTS[j]);
          }
          if (!open.length) reason = 'full';
        }
        days.push({ iso: iso, slots: open, ok: !reason, reason: reason });
      }

      /* never let the strip start or end on a dead chip */
      while (days.length && !days[0].ok) days.shift();
      while (days.length && !days[days.length - 1].ok) days.pop();
      return days;
    }

    function firstOpen(cal) {
      for (var i = 0; i < cal.length; i++) if (cal[i].ok && cal[i].slots.length) return cal[i];
      return null;
    }

    w.ShowqCalendar = { build: build, firstOpen: firstOpen, parseSlots: parseSlots };
  }

  /* Consumers that were parsed before this file executed wait on this. */
  try { document.dispatchEvent(new CustomEvent('showq:time:ready')); } catch (e) {}
})(window);
